<template>
  <v-card>
    <v-toolbar dark color="secondary" dense>
      <v-toolbar-title>
        <span class="body-2 text-uppercase">{{data.name}}</span>
      </v-toolbar-title>

      <v-layout fill-height justify-end align-center>
        <v-tooltip dark color="secondary" bottom>
          <v-btn icon flat slot="activator" 
          :data-testid="$options.name + '__btn__' + 'close'"
          @click="() => {$emit('close')}">
            <svg-icon
              :color="'white'"
              :name="'close'"
              width="20"
            />
          </v-btn>
          <span>{{ $t('close') }}</span>
        </v-tooltip>
      </v-layout>
    </v-toolbar>

    <v-card-text>
      <line-chart
        :data="history"
        :measure="data"
      />
    </v-card-text>

    <v-card-actions>
      <v-layout column fill-height>
        <v-layout pa-1>
          <date-picker
            :value="date.start"
            :label="'date start'"
            :max="date.end"
            @change="(payload)=>{date.start = payload}"
          />
          <date-picker
            v-model="date.end"
            :label="'date end'"
            :min="date.start"
            @change="(payload)=>{date.end = payload}"
          />
        </v-layout>
        <v-layout pa-1 pl-3>
          <v-btn :color="params.name == 'h' ? 'primary' : 'accent'" small :data-testid="$options.name + '__btn__' + 'hours'" @click="()=>{createParams(preset.hours)}">{{ $t('2 hours') }}</v-btn>
          <v-btn :color="params.name == 'd' ? 'primary' : 'accent'" small :data-testid="$options.name + '__btn__' + 'day'" @click="()=>{createParams(preset.day)}">{{ $t('1 day') }}</v-btn>
          <v-btn :color="params.name == 'w' ? 'primary' : 'accent'" small :data-testid="$options.name + '__btn__' + 'week'" @click="()=>{createParams(preset.week)}">{{ $t('1 week') }}</v-btn>
          <v-btn :color="params.name == 'M' ? 'primary' : 'accent'" small :data-testid="$options.name + '__btn__' + 'mounth'" @click="()=>{createParams(preset.mounth)}">{{ $t('1 mounth') }}</v-btn>
        </v-layout>

      </v-layout>

    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'
import datePicker from '@/nms/components/controls/input-menu-datepicker.vue'
import lineChart from '@/nms/pages/equipment/chart.vue'
import svgIcon from '@/nms/components/media/svg/static/svg-icon.vue'

// Типы
interface PresetItem {
  name: string
  value: number
  step: number
}

interface Presets {
  hours: PresetItem
  day: PresetItem
  week: PresetItem
  mounth: PresetItem
}

interface Params {
  from: any
  to: any
  step: number | null
  measureId: string | null
  name?: string
}

interface DateRange {
  start: any
  end: any
}

interface MeasureData {
  $id?: string
  value?: any
  [key: string]: any
}

interface EquipmentData {
  $id?: string
  [key: string]: any
}

interface HistoryItem {
  time: string
  value: number
  [key: string]: any
}

export default Vue.extend({
  name: 'CHART_CARD',

  components: {
    datePicker,
    lineChart,
    svgIcon
  },

  props: {
    data: {
      type: Object as () => MeasureData,
      default: () => ({})
    },
    equipment: {
      type: Object as () => EquipmentData,
      default: () => ({})
    },
  },

  data(): {
    measure: MeasureData
    history: HistoryItem[]
    params: Params
    preset: Presets
    date: DateRange
  } {
    return {
      measure: {},
      history: [],
      params: {
        from: null,
        to: null,
        step: null,
        measureId: null,
        name: 'h'
      },
      preset: {
        hours: {
          name: 'h',
          value: 2,
          step: 75
        },
        day: {
          name: 'd',
          value: 1,
          step: 900
        },
        week: {
          name: 'w',
          value: 1,
          step: 6000
        },
        mounth: {
          name: 'M',
          value: 1,
          step: 26000
        },
      },
      date: {
        start: null,
        end: null,
      }
    }
  },

  watch: {
    data: {
      handler(newVal: MeasureData): void {
        console.log('WATCH', this.$options.name, 'data', this.data)
        this.measure = newVal
      },
      deep: true
    },
    date: {
      handler(newVal: DateRange): void {
        if (newVal.start && newVal.end) {
          this.rangeParams(newVal)
        }
      },
      deep: true
    },
  },

  created(): void {
    this.measure = this.data
    this.createParams()
  },

  methods: {
    createParams(val?: PresetItem): void {
      this.date.start = null
      this.date.end = null

      if (!val) {
        this.params = {
          from: (this as any).$moment().add(-2, 'h'),
          to: (this as any).$moment(),
          step: 75,
          measureId: this.measure.$id || null,
          name: 'h'
        }
      } else {
        this.params = {
          from: (this as any).$moment().add(-val.value, val.name),
          to: (this as any).$moment(),
          step: val.step,
          measureId: this.measure.$id || null,
          name: val.name
        }
      }
      this.loadHistory()
    },

    rangeParams(range: DateRange): void {
      const duration = (this as any).$moment(range.end).diff((this as any).$moment(range.start))
      const days = (this as any).$moment.duration(duration).asDays()
      console.log(this.$options.name, 'rangeParams', 'days$', days)

      const step = 900 * days
      this.params = {
        from: (this as any).$moment(range.start),
        to: (this as any).$moment(range.end),
        step: step,
        measureId: this.measure.$id || null
      }
      console.log(this.$options.name, 'rangeParams', 'this.params', this.params)
      this.loadHistory()
    },

    async loadHistory(): Promise<void> {
      try {
        const device$ = await this.$bridge.getObjectById(this.equipment.$id)
        console.log(this.$options.name, 'loadHistory', 'device$', device$)

        this.history = []
        this.history = await device$.getMeasuresHistory(
          this.params.from,
          this.params.to,
          this.params.step,
          this.params.measureId
        )
        console.log(this.$options.name, 'loadHistory', 'history', this.history)
      } catch (error) {
        console.error('CATCH', this.$options.name, 'loadHistory', 'SMTH WRONG', error)
      }
    },
  },
})
</script>

<style lang="css" scoped>
</style>
